import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, LayoutGrid, User, Phone, ShoppingCart, Store, Shield, Info, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/use-toast';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { logout } from '@/state/slices/authSlice';
import { useBrand, useTenant } from '@/hooks';
import { MunshiLogo } from '@/munshi/components/MunshiLogo';
import { Kirana360Logo } from '@/components/ui/Kirana360Logo';

import { IS_MOBILE_APP } from '@/constants/env';
import LanguageToggle from '@/components/ui/LanguageToggle';
import { setAppLanguage, selectAppLanguage, selectShowLanguageToggle } from '@/state/slices/userPreferenceSlice';
import { SupportedLanguage } from '@/constants/supportedLanguages';
import { selectCurrentStore } from '@/state/slices/storesSlice';

// Utility function to format phone number with proper spacing
const formatPhoneNumber = (phone: string): string => {
  if (!phone) return 'No phone number';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format Indian phone numbers (10 digits)
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }
  
  // Format with country code (11+ digits)
  if (cleaned.length >= 11) {
    const countryCode = cleaned.slice(0, -10);
    const number = cleaned.slice(-10);
    return `+${countryCode} ${number.slice(0, 5)} ${number.slice(5)}`;
  }
  
  // Return as is if doesn't match expected patterns
  return phone;
};

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, publicUser } = useAppSelector((state) => state.auth);
  const { toast } = useToast();
  const brand = useBrand();
  const tenant = useTenant();
  const currentLanguage = useAppSelector(selectAppLanguage);
  const store = useAppSelector(selectCurrentStore);
  const localLanguage = (store?.language_locale?.toLowerCase() as SupportedLanguage) || 'hindi';
  const showLanguageToggle = useAppSelector(selectShowLanguageToggle);
  const location = useLocation();
  const isIntro = location.pathname === '/intro' || (location.pathname === '/' && !user);

  const handleLanguageChange = (lang: SupportedLanguage) => {
    dispatch(setAppLanguage(lang));
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate('/login', { replace: true });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to logout',
        variant: 'destructive',
      });
    }
  };

  if (!user) return null;

  // Logo rendering logic based on brand configuration
  let logoNode;
  if (brand.logo.useCustomLogo && brand.logo.component === 'MunshiLogo') {
    logoNode = <MunshiLogo size={28} />;
  } else {
    // Default store icon for Kirana360 and fallback
    logoNode = <Kirana360Logo size={20} />;
  }

  return (
    <nav className={isIntro ? 'bg-white/60 backdrop-blur-xl border-b border-indigo-200/30 shadow-sm' : 'bg-white/95 backdrop-blur-md border-b border-indigo-100/60 shadow-[0_1px_3px_rgba(67,56,202,0.06)]'}>
      <div className="container mx-auto px-3 sm:px-4">
        <div className={`flex items-center justify-between ${IS_MOBILE_APP ? 'h-16 sm:h-16' : 'h-14 sm:h-16'}`}>
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2.5 group">
              <div className="relative">
                {logoNode}
                <div className="absolute -inset-1.5 bg-indigo-400/10 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <span className="text-lg sm:text-xl font-bold tracking-tight text-stone-800">{brand.name}</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:gap-2">
            {tenant === 'seller' && (
              <Link to="/stores" className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-stone-500 hover:text-indigo-700 hover:bg-indigo-50/60 rounded-lg transition-all duration-200">
                <LayoutGrid className="h-4 w-4" />
                All Stores
              </Link>
            )}
            
            {/* Switch Tenant Button */}
            {tenant === 'seller' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentUrl = new URL(window.location.href);
                  currentUrl.searchParams.set('tenant', 'buyer');
                  window.location.href = currentUrl.toString();
                }}
                className="flex items-center text-blue-700 border-blue-200 hover:bg-blue-50 hover:border-blue-300 transition-all duration-200"
              >
                <ShoppingCart className="h-4 w-4 mr-1.5" />
                Buyer View
              </Button>
            ) : tenant === 'buyer' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentUrl = new URL(window.location.href);
                  currentUrl.searchParams.set('tenant', 'seller');
                  window.location.href = currentUrl.toString();
                }}
                className="flex items-center text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-200"
              >
                <Store className="h-4 w-4 mr-1.5" />
                Seller View
              </Button>
            ) : null}
            
            {/* Zeus Admin Button - Only show for superadmins */}
            {publicUser?.is_superadmin && tenant !== 'zeus' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentUrl = new URL(window.location.href);
                  currentUrl.searchParams.set('tenant', 'zeus');
                  window.location.href = currentUrl.toString();
                }}
                className="flex items-center text-purple-600 border-purple-200 hover:bg-purple-50 hover:border-purple-300 transition-all duration-200"
              >
                <Shield className="h-4 w-4 mr-1.5" />
                Admin
              </Button>
            )}
            
            {/* Language Toggle - Only show when requested by page */}
            {showLanguageToggle && (
              <LanguageToggle 
                value={currentLanguage} 
                onChange={handleLanguageChange} 
                localLanguage={localLanguage}
                className="w-20 h-8"
              />
            )}
            
            {/* Tutorial Video Button */}
            <button
              onClick={() => window.open('https://www.youtube.com/watch?v=24kFeRgG-hk', '_blank', 'noopener,noreferrer')}
              title="Watch tutorial video"
              className="h-8 w-8 flex items-center justify-center rounded-lg text-stone-400 hover:text-indigo-600 hover:bg-indigo-50/60 transition-all duration-200"
            >
              <Info className="h-5 w-5" />
            </button>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-100 to-blue-100 border border-indigo-200/50 flex items-center justify-center hover:shadow-md hover:shadow-indigo-100/50 transition-all duration-200 ml-1">
                  <User className="h-4 w-4 text-indigo-700" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 border-indigo-100/60 shadow-lg shadow-indigo-900/5">
                <div className="px-3 py-2.5 bg-gradient-to-r from-indigo-50/80 to-blue-50/60 rounded-t-md -mt-1 -mx-1 mb-1">
                  <p className="text-sm font-semibold text-stone-800">{user.user_metadata?.user_number || user.phone || publicUser?.phone || 'No phone number'}</p>
                  {user.email && (
                    <p className="text-xs text-stone-500 mt-0.5">{user.email}</p>
                  )}
                </div>
                <DropdownMenuSeparator className="bg-indigo-100/40" />
                <DropdownMenuItem className="cursor-pointer focus:bg-indigo-50/60" onClick={() => {
                  const supportMessage = `Hi, I need help or have a query regarding ${brand.name}`;
                  window.open(`https://wa.me/919353617115?text=${encodeURIComponent(supportMessage)}`, '_blank');
                }}>
                  <Phone className="mr-2 h-4 w-4 text-green-600" />
                  <span>Contact</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-indigo-100/40" />
                <DropdownMenuItem className="cursor-pointer focus:bg-indigo-50/60" onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4 text-stone-500" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-indigo-100/40" />
                <DropdownMenuItem className="cursor-pointer text-red-600 focus:bg-red-50/60" onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center gap-1.5">
            {/* Language Toggle - Only show when requested by page */}
            {showLanguageToggle && (
              <LanguageToggle 
                value={currentLanguage} 
                onChange={handleLanguageChange} 
                localLanguage={localLanguage}
                className="scale-[0.8]"
              />
            )}
            {/* Tutorial Video Button - Mobile */}
            <button
              onClick={() => window.open('https://www.youtube.com/watch?v=24kFeRgG-hk', '_blank', 'noopener,noreferrer')}
              title="Watch tutorial video"
              className="h-8 w-8 flex items-center justify-center rounded-lg text-stone-400 hover:text-indigo-600 hover:bg-indigo-50/60 transition-all duration-200"
            >
              <Info className="h-5 w-5" />
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="h-8 w-8 flex items-center justify-center rounded-lg text-stone-500 hover:text-indigo-700 hover:bg-indigo-50/60 transition-all duration-200"
            >
              {isOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden bg-white border-t border-indigo-100/60 shadow-lg shadow-indigo-900/5 rounded-b-xl overflow-hidden">
            <div>
              {/* User Info */}
              <div className="px-4 py-4 bg-gradient-to-r from-indigo-50/80 to-blue-50/50 border-b border-indigo-100/40">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-full flex items-center justify-center shadow-sm shadow-indigo-200/50">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-stone-800 truncate">
                      {formatPhoneNumber(user.user_metadata?.user_number || user.phone || publicUser?.phone || '')}
                    </p>
                    {user.email && (
                      <p className="text-sm text-stone-500 truncate">{user.email}</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Switch Tenant Button - Mobile */}
              {tenant === 'seller' ? (
                <button
                  className="flex items-center px-4 py-3.5 text-sm font-medium text-blue-700 hover:bg-blue-50/60 w-full transition-colors duration-200"
                  style={{ background: 'none', border: 'none' }}
                  onClick={() => {
                    setIsOpen(false);
                    const currentUrl = new URL(window.location.href);
                    currentUrl.searchParams.set('tenant', 'buyer');
                    window.location.href = currentUrl.toString();
                  }}
                >
                  <ShoppingCart className="h-5 w-5 mr-3 text-blue-600" />
                  Switch to Buyer View
                </button>
              ) : (
                <button
                  className="flex items-center px-4 py-3.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50/60 w-full transition-colors duration-200"
                  style={{ background: 'none', border: 'none' }}
                  onClick={() => {
                    setIsOpen(false);
                    const currentUrl = new URL(window.location.href);
                    currentUrl.searchParams.set('tenant', 'seller');
                    window.location.href = currentUrl.toString();
                  }}
                >
                  <Store className="h-5 w-5 mr-3 text-emerald-600" />
                  Switch to Seller View
                </button>
              )}

              {tenant === 'seller' && (
                <Link
                  to="/stores"
                  className="flex items-center px-4 py-3.5 text-sm font-medium text-stone-600 hover:bg-indigo-50/60 hover:text-indigo-700 transition-colors duration-200"
                  onClick={() => setIsOpen(false)}
                >
                  <LayoutGrid className="h-5 w-5 mr-3 text-stone-400" />
                  All Stores
                </Link>
              )}

              <Link
                to="/profile"
                className="flex items-center px-4 py-3.5 text-sm font-medium text-stone-600 hover:bg-indigo-50/60 hover:text-indigo-700 transition-colors duration-200"
                onClick={() => setIsOpen(false)}
              >
                <User className="h-5 w-5 mr-3 text-stone-400" />
                Profile
              </Link>
              
              <button
                className="flex items-center px-4 py-3.5 text-sm font-medium text-green-700 hover:bg-green-50/60 w-full transition-colors duration-200"
                style={{ background: 'none', border: 'none' }}
                onClick={() => {
                  const supportMessage = `Hi, I need help or have a query regarding ${brand.name}.`;
                  window.open(`https://wa.me/919353617115?text=${encodeURIComponent(supportMessage)}`, '_blank');
                }}
              >
                <Phone className="h-5 w-5 mr-3 text-green-600" />
                Support
              </button>

              <div className="border-t border-indigo-100/40 mt-1">
                <button
                  className="flex items-center px-4 py-3.5 text-sm font-medium text-red-600 hover:bg-red-50/60 w-full transition-colors duration-200"
                  style={{ background: 'none', border: 'none' }}
                  onClick={() => {
                    setIsOpen(false);
                    handleLogout();
                  }}
                >
                  <LogOut className="h-5 w-5 mr-3 text-red-500" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
